import { useCallback, useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { getFirstPageData, getNextPageData } from '../../api/commentsPaginatedData';
import type { Comment } from '../../types';

export function useCommentsPaginatedData(slug: string) {
	const utilityContext = useUtilityContext();

	const { data, isLoading, isError, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery({
		queryKey: ['starter-dashboard-comments-paginated', slug],
		queryFn: async ({ pageParam }) =>
			pageParam == null ? getFirstPageData(utilityContext, slug) : getNextPageData(utilityContext, pageParam, slug),
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) => (lastPage.hasNextPage && lastPage.cursor != null ? lastPage.cursor : undefined),
		staleTime: Infinity,
		refetchOnWindowFocus: false,
	});

	const comments = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

	const loadMore = useCallback(() => {
		void fetchNextPage();
	}, [fetchNextPage]);

	return {
		data: {
			comments: comments as readonly Comment[],
			hasNextPage,
			nextPageLoading: isFetchingNextPage,
			isLoading,
			isError,
		},
		actions: { loadMore },
	};
}
